import { useCallback, useEffect, useState } from 'react';
import api from './client';

/**
 * Fetches predictive module context for the SPA shell.
 *
 * @param {Object} [params]
 * @param {string|number} [params.countyId]
 * @param {Object} [params.filters]
 * @param {Object} [options]
 * @param {boolean} [options.skip=false] When true, bypasses the fetch.
 * @returns {{data: Object|null, isLoading: boolean, error: Error|null, refresh: () => Promise<Object>}}
 */
export function usePredictiveContext({ countyId, filters } = {}, { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(!skip);
  const [error, setError] = useState(null);

  const fetchContext = useCallback(async () => {
    return api.predictive.getContext({
      countyId,
      filters,
    });
  }, [countyId, filters]);

  useEffect(() => {
    if (skip) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const response = await fetchContext();
        if (!cancelled) {
          setData(response);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchContext, skip]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchContext();
      setData(response);
      setError(null);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchContext]);

  return {
    data,
    isLoading,
    error,
    refresh,
  };
}
